/*eslint-disable*/
import React, { useEffect, useMemo, useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    MenuItem,
    Select,
    FormControl,
    Grid,
    Box,
    CircularProgress,
} from "@mui/material";

import {
    DataGridPremium,
    GridColDef,
    GridToolbar,
    useGridApiRef,
    useKeepGroupedColumnsHidden,
} from "@mui/x-data-grid-premium";
import { BarChart } from "@mui/x-charts/BarChart"; // Import BarChart
import { SelectChangeEvent } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import FastAPIClient from "../../client";
import config from "../../config";
import { blueberryTwilightPalette } from "@mui/x-charts/colorPalettes";

import CustomBarChart from "./CustomBarChart"; // Import the CustomBarChart
import StatCard from "../LayoutComponents/StatCard";

import { frFR } from "@mui/x-data-grid/locales";

const client = new FastAPIClient(config);

type UserClientVisitLog = {
    user_id: string;
    first_name: string;
    last_name: string;
    warehouse_name: string;
    team_name: string;
    client_name: string;
    visit_date: string;
    visit_time: string;
};

interface UserKilometerProps {
    // selectedUser: string;
    // selectedTeam: string;
    // selectedWarehouse: string;
    // timePeriod: string;
}

const UserVisitsTable: React.FC<UserKilometerProps> = ({}) => {
    const [data, setData] = useState<UserClientVisitLog[] | null>(null);

    // const [selectedUser, setSelectedUser] = useState<string>("all");
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
        dayjs().startOf("week"),
        dayjs().endOf("week"),
    ]);
    // const [timePeriod, setTimePeriod] = useState<string>("last7Days");
    // const [selectedTeam, setSelectedTeam] = useState<string>("all");
    // const [selectedWarehouse, setSelectedWarehouse] = useState<string>("all");

    const fetchClientVisits = async () => {
        try {
            const response = await client.fetchUserClientVisits(); // new method
            console.log("client visits", response.results);
            setData(response.results);
        } catch (error) {
            console.error("Error fetching client visits:", error);
        }
    };

    useEffect(() => {
        fetchClientVisits();
    }, []);

    const apiRef = useGridApiRef();

    const initialState = useKeepGroupedColumnsHidden({
        apiRef,
        initialState: {
            rowGrouping: {
                model: ["salesRep", "date"],
            },
            sorting: {
                sortModel: [{ field: "date", sort: "desc" }],
            },
        },
    });

    const uniqueTeams = useMemo(() => {
        if (!data) return [];
        const teams = new Set(data.map((d) => d.team_name).filter(Boolean));
        return Array.from(teams);
    }, [data]);

    const uniqueWarehouses = useMemo(() => {
        if (!data) return [];
        const warehouses = new Set(
            data.map((d) => d.warehouse_name).filter(Boolean)
        );
        return Array.from(warehouses);
    }, [data]);

    // useEffect(() => {
    //     switch (timePeriod) {
    //         case "last7Days":
    //             setDateRange([
    //                 dayjs().subtract(6, "day").startOf("day"),
    //                 dayjs().endOf("day"),
    //             ]);
    //             break;
    //         case "last30Days":
    //             setDateRange([
    //                 dayjs().subtract(29, "day").startOf("day"),
    //                 dayjs().endOf("day"),
    //             ]);
    //             break;
    //         case "lastMonth":
    //             setDateRange([
    //                 dayjs().subtract(1, "month").startOf("month"),
    //                 dayjs().subtract(1, "month").endOf("month"),
    //             ]);
    //             break;
    //         default:
    //             setDateRange([dayjs().startOf("week"), dayjs().endOf("week")]);
    //             break;
    //     }
    // }, [timePeriod]);

    // const handleTimePeriodChange = (event: SelectChangeEvent<string>) => {
    //     setTimePeriod(event.target.value);
    // };

    const handleDateRangeChange = (newValue: [Dayjs | null, Dayjs | null]) => {
        setDateRange(newValue);
    };

    const uniqueUsers = useMemo(() => {
        if (!data) return [];
        const userMap = new Map<string, UserClientVisitLog>();

        data.forEach((user) => {
            const fullName = `${user.first_name} ${user.last_name}`;
            if (!userMap.has(fullName)) {
                userMap.set(fullName, user);
            }
        });

        return Array.from(userMap.values());
    }, [data]);

    const filteredData = data;

    const chartData = useMemo(() => {
        if (!filteredData || !dateRange[0] || !dateRange[1])
            return { labels: [], values: [], userNames: [] };

        const [start, end] = dateRange;

        const allDates: string[] = [];
        let cursor = start.startOf("day");

        while (cursor.isBefore(end) || cursor.isSame(end, "day")) {
            allDates.push(cursor.format("YYYY-MM-DD"));
            cursor = cursor.add(1, "day");
        }

        // Prepare a map of kilometers by user per day (group by date and user)
        const valueMap = new Map<
            string,
            Map<
                string,
                {
                    minTime: string;
                    minKilometers: number;
                    maxTime: string;
                    maxKilometers: number;
                }
            >
        >();

        filteredData.forEach((entry) => {
            const entryDate = dayjs(entry.visit_date).format("YYYY-MM-DD");
            if (!valueMap.has(entryDate)) {
                valueMap.set(entryDate, new Map());
            }

            const dailyData = valueMap.get(entryDate);
            if (dailyData) {
                const userId = entry.user_id;
                const currentData = dailyData.get(userId) || {
                    minTime: "",
                    minKilometers: Infinity,
                    maxTime: "",
                    maxKilometers: -Infinity,
                };

                // Determine if the current entry should update min or max time and kilometers
                const currentTime = entry.visit_date;
                // const currentKilometers = entry.kilometers;

                // if (
                //     currentTime < currentData.minTime ||
                //     currentData.minTime === ""
                // ) {
                //     currentData.minTime = currentTime;
                //     currentData.minKilometers = currentKilometers;
                // }
                // if (
                //     currentTime > currentData.maxTime ||
                //     currentData.maxTime === ""
                // ) {
                //     currentData.maxTime = currentTime;
                //     currentData.maxKilometers = currentKilometers;
                // }

                dailyData.set(userId, currentData);
            }
        });

        // Now calculate the actual kilometers as the difference between max and min for each user on each day
        const labels = allDates;

        // Create an array of user names (first_name and last_name)
        const userNames = Array.from(
            new Set(
                filteredData.map(
                    (entry) => `${entry.first_name} ${entry.last_name}`
                )
            )
        );

        // Calculate the values (kilometers difference between the max and min for each user)
        const values = allDates.map((date) => {
            const dailyData = valueMap.get(date);
            if (!dailyData) return userNames.map(() => 0); // No data for this day

            return userNames.map((fullName) => {
                const user = filteredData.find(
                    (entry) =>
                        `${entry.first_name} ${entry.last_name}` === fullName
                );
                if (user) {
                    const data = dailyData.get(user.user_id);
                    if (data) {
                        // Calculate the difference between the max and min kilometers
                        return data.maxKilometers - data.minKilometers;
                    }
                }
                return 0; // If no data exists, return 0 kilometers
            });
        });

        return { labels, values, seriesLabels: userNames };
    }, [filteredData, dateRange]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box mt={4} style={{ height: 500, width: "100%" }}>
                <DataGridPremium
                    rows={(data || []).map((row, index) => ({
                        id: index,
                        salesRep: `${row.first_name} ${row.last_name}`,
                        date: row.visit_date,
                        time: row.visit_time,
                        client: row.client_name,
                    }))}
                    columns={
                        [
                            {
                                field: "salesRep",
                                headerName: "Commercial",
                                flex: 1,
                            },
                            { field: "date", headerName: "Date", flex: 1 },
                            { field: "client", headerName: "Client", flex: 1 },
                        ] as GridColDef[]
                    }
                    showToolbar
                    disableRowSelectionOnClick
                    apiRef={apiRef}
                    initialState={initialState}
                    rowGroupingColumnMode="multiple"
                    sx={{
                        backgroundColor: "#fff",
                        borderRadius: 2,
                        boxShadow: "none",
                    }}
                    localeText={
                        frFR.components.MuiDataGrid.defaultProps.localeText
                    }
                    slotProps={{
                        toolbar: {
                            csvOptions: {
                                fileName: `Visites_Clients_${dayjs().format(
                                    "YYYY-MM-DD"
                                )}`,
                            },
                            excelOptions: {
                                fileName: `Visites_Clients_${dayjs().format(
                                    "YYYY-MM-DD"
                                )}`,
                            },
                        },
                    }}
                />
            </Box>
        </LocalizationProvider>
    );
};

export default UserVisitsTable;
